import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { translations, detectBrowserLanguage } from '../i18n/translations';

const BACKEND_URL = window.location.origin;
const API = `/api`;


const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Auth state
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [tenantName, setTenantName] = useState("Demo");
  const [appVersion, setAppVersion] = useState(null);

  // UI state
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem('language') || detectBrowserLanguage();
  });
  const [theme, setThemeState] = useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  // Translation helper
  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }, [language]);

  // Set axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Apply theme
  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Save language
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  // Get Tenant name
  useEffect(() => {
    const fetchAppConfig = async () => {
      try {
        const response = await axios.get(`${API}/config`);
        if (response.data && response.data.tenant_name) {
          setTenantName(response.data.tenant_name);
        }
        if (response.data && response.data.version) {
          setAppVersion(response.data.version);
        }
      } catch (error) {
        console.warn("Could not fetch app config, using default name:", error);
      }
    };
    fetchAppConfig();
  }, []);

  // Auth functions
  const login = async (loginData) => {
    const response = await axios.post(`${API}/auth/login`, loginData);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (formData) => {
    const response = await axios.put(`${API}/auth/profile`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    setUser(response.data);
    return response.data;
  };

  const changePassword = async (passwordData) => {
    const formData = new FormData();
    formData.append('current_password', passwordData.currentPassword);
    formData.append('new_password', passwordData.newPassword);
    await axios.put(`${API}/auth/password`, formData);
  };

  // Theme and language setters
  const setTheme = (newTheme) => {
    setThemeState(newTheme);
  };

  const setLanguage = (newLanguage) => {
    setLanguageState(newLanguage);
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  // Role checks
  const isAdmin = user?.profile_type === 'administrator';
  const isEditor = user?.profile_type === 'editor';
  const isViewer = user?.profile_type === 'viewer';
  const canEdit = isAdmin || isEditor;

  const value = {
    // Auth
    user,
    token,
    loading,
    login,
    logout,
    updateProfile,
    changePassword,
    isAdmin,
    isEditor,
    isViewer,
    canEdit,
    // UI
    language,
    setLanguage,
    theme,
    setTheme,
    toggleTheme,
    t,
    // API
    API,
    // Tenant
    tenantName,
    appVersion,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
